import { BalanceType } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { useTranslation } from '@frontend/i18n';
import {
  Button,
  CheckboxField,
  Chip,
  PopoverDialog,
  SearchField,
  Text,
  useDebouncedValue,
  useDidUpdate,
  useForm,
  useFormField,
  usePopoverDialog,
} from '@frontend/design-system';
import { useBulkCollectionsFilterShallowStore } from '../../../../store/bulk-collections-filter-store';
import { BalanceTag } from '../../common/BalanceTag';
import { styles } from '../styles';

export const FiltersOnMobile = () => {
  const { t } = useTranslation('payments');
  const { getTriggerProps, getDialogProps, close } = usePopoverDialog({
    placement: 'bottom',
  });
  const { balanceType, searchValue, setBalanceType, setSearchValue } = useBulkCollectionsFilterShallowStore(
    'balanceType',
    'searchValue',
    'setBalanceType',
    'setSearchValue'
  );
  const globalSearch = useFormField({ type: 'text', value: searchValue }, []);
  const debouncedValue = useDebouncedValue(globalSearch.value, 300);

  const { getFieldProps, values, seedValues } = useForm({
    fields: {
      newBalance: { type: 'checkbox', value: balanceType.includes(BalanceType.BALANCE_TYPE_NEW) },
      changedBalance: { type: 'checkbox', value: balanceType.includes(BalanceType.BALANCE_TYPE_UPDATE) },
      existingBalance: { type: 'checkbox', value: balanceType.includes(BalanceType.BALANCE_TYPE_EXISTS) },
    },
  });

  useDidUpdate(() => {
    setSearchValue(debouncedValue);
  }, [debouncedValue]);

  const handleApply = () => {
    const selectedBalanceTypes = [
      values.newBalance && BalanceType.BALANCE_TYPE_NEW,
      values.changedBalance && BalanceType.BALANCE_TYPE_UPDATE,
      values.existingBalance && BalanceType.BALANCE_TYPE_EXISTS,
    ].filter(Boolean) as BalanceType[];
    setBalanceType(selectedBalanceTypes);
    close();
  };

  const handleSelectAll = () => {
    seedValues({ newBalance: true, changedBalance: true, existingBalance: true });
  };

  return (
    <div css={styles.filtersOnMobile.container}>
      <Chip.DropdownFilter
        filterIsActive={!!balanceType.length}
        {...getTriggerProps()}
        css={styles.filtersOnMobile.dropdownFilter}
      >
        <Text size='medium'>{t('Collection Status')}</Text>
      </Chip.DropdownFilter>
      <div css={styles.filtersOnMobile.searchWrapper}>
        <SearchField
          placeholder={'Search'}
          name='Global Table Search'
          data-trackingid={'tableTrackingIds.globalSearch'}
          {...globalSearch}
          css={styles.filtersOnMobile.searchField}
        />
      </div>
      <PopoverDialog {...getDialogProps()} css={styles.filtersOnMobile.popoverDialog}>
        <div css={styles.filtersOnMobile.actionButtonsRow}>
          <Button variant='tertiary' onClick={handleSelectAll} trackingId='collections-mobile-filter-select-all-button'>
            {t('Select All')}
          </Button>
          <Button
            variant='tertiary'
            onClick={() => {
              seedValues({ newBalance: false, changedBalance: false, existingBalance: false });
            }}
            disabled={!values.newBalance && !values.changedBalance && !values.existingBalance}
            trackingId='collections-mobile-filter-clear-button'
          >
            {t('Clear')}
          </Button>
        </div>
        <CheckboxField
          {...getFieldProps('newBalance')}
          label={<BalanceTag balanceType={BalanceType.BALANCE_TYPE_NEW} maxWidth='300px' />}
        />
        <CheckboxField
          {...getFieldProps('changedBalance')}
          label={<BalanceTag balanceType={BalanceType.BALANCE_TYPE_UPDATE} maxWidth='300px' />}
        />
        <CheckboxField
          {...getFieldProps('existingBalance')}
          label={<BalanceTag balanceType={BalanceType.BALANCE_TYPE_EXISTS} maxWidth='300px' />}
        />
        <Button
          css={styles.filtersOnMobile.applyButton}
          onClick={handleApply}
          trackingId='collections-mobile-filter-apply-button'
        >
          {t('Apply')}
        </Button>
      </PopoverDialog>
    </div>
  );
};
